/**
 * A picture or a film from the thread, over the chat (the canon's `.lb`). A Base UI modal, so focus
 * is held inside it, Escape closes it and focus returns to the bubble that opened it.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';

import { useSignedUrl } from '../lib/useSignedUrl';

import { CloseGlyph } from './ChatGlyphs';
import type { MediaView } from './MessageBubble';
import styles from './chat.module.css';

export function MediaLightbox({ view, onClose }: { readonly view: MediaView | null; readonly onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={view !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.lightboxScrim} />
        <Dialog.Popup className={styles.lightbox}>
          <div className={styles.lightboxHead}>
            <Dialog.Title className={styles.lightboxTitle}>
              {view?.kind === 'video' ? t('web:chat.lightbox.video') : t('web:chat.lightbox.image')}
            </Dialog.Title>
            <Dialog.Close className={styles.popIcon} aria-label={t('web:chat.lightbox.close')}>
              <CloseGlyph size={18} />
            </Dialog.Close>
          </div>
          {view === null ? null : <LightboxMedia view={view} />}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function LightboxMedia({ view }: { readonly view: MediaView }) {
  const signed = useSignedUrl(view.mediaId, view.url, 'primary');
  if (signed.url === null) return null;
  if (view.kind === 'image') {
    return <img className={styles.lightboxMedia} src={signed.url} alt="" onError={signed.onError} />;
  }
  return (
    <video
      className={styles.lightboxMedia}
      src={signed.url}
      poster={view.poster ?? undefined}
      controls
      autoPlay
      playsInline
      onError={signed.onError}
    />
  );
}
